import { ReactNode, FC, useEffect, useState } from 'react';
import classnames from 'classnames';

import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { ChevronLeftOutlinedSize24, ChevronRightOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { NumberPagesAction } from '@hh.ru/magritte-ui-number-pages/NumberPagesAction';
import type { NumberPagesRenderItem, NumberPagesProps } from '@hh.ru/magritte-ui-number-pages/types';
import { usePages, PageControls } from '@hh.ru/magritte-ui-number-pages/usePages';

import styles from './styles.less';

const baseRenderItem: NumberPagesRenderItem = ({ isPrevious, isNext, ...props }) => {
    return <NumberPagesAction {...props} isIcon={isPrevious || isNext} />;
};

const getPropsByScreenSize = (isXS: boolean, isS: boolean): number[] => {
    if (isXS) {
        return [0, 0];
    } else if (isS) {
        return [1, 1];
    }
    return [2, 1];
};

export const NumberPages: FC<NumberPagesProps> = ({
    first,
    last,
    current,
    onChange,
    'aria-label': ariaLabel,
    renderItem = baseRenderItem,
    renderNavigation = baseRenderItem,
    getAriaLabelItem,
    ...props
}) => {
    const { isXS, isS } = useBreakpoint();
    const [initialSiblingCount, initialBoundaryCount] = getPropsByScreenSize(isXS, isS);
    const [siblingCount, setSiblingCount] = useState(initialSiblingCount);
    const [boundaryCount, setBoundaryCount] = useState(initialBoundaryCount);
    const pages = usePages({ first, last, current, siblingCount, boundaryCount });

    useEffect(() => {
        const [siblingCount, boundaryCount] = getPropsByScreenSize(isXS, isS);
        setSiblingCount(siblingCount);
        setBoundaryCount(boundaryCount);
    }, [isXS, isS]);

    if (first > last) {
        throw new Error(
            `Magritte component NumberPages: First prop more than last prop. first: ${first}, last: ${last}`
        );
    }

    if (current < first || current > last) {
        throw new Error(
            `Magritte component NumberPages: Current is wrong. current: ${current}, first: ${first}, last: ${last}`
        );
    }

    if (last - first < 1) {
        return null;
    }

    const renderPageItem = (item: number): ReactNode => {
        const isSelected = item === current;

        const baseProps = {
            selected: isSelected,
            key: item,
            'aria-label': getAriaLabelItem?.(item),
        };

        switch (item) {
            case PageControls.Previous:
                return renderNavigation({
                    ...baseProps,
                    'data-qa': 'number-pages-previous',
                    onClick: () => {
                        onChange?.(current - 1);
                    },
                    isPrevious: true,
                    page: current - 1,
                    children: <ChevronLeftOutlinedSize24 />,
                });
            case PageControls.StartEllipsis:
            case PageControls.EndEllipsis:
                return (
                    <li key={item} data-qa="number-pages-ellipsis" className={styles.numberPagesEllipsis}>
                        ...
                    </li>
                );
            case PageControls.Next:
                return renderNavigation({
                    ...baseProps,
                    'data-qa': 'number-pages-next',
                    onClick: () => {
                        onChange?.(current + 1);
                    },
                    isNext: true,
                    page: current + 1,
                    children: <ChevronRightOutlinedSize24 />,
                });
            default:
                return renderItem({
                    ...baseProps,
                    onClick: () => {
                        if (!isSelected) {
                            onChange?.(item);
                        }
                    },
                    'data-qa': `number-pages-${item} ${isSelected ? 'number-pages-selected' : ''}`,
                    page: item,
                    selected: isSelected,
                    children: item,
                });
        }
    };

    return (
        <nav
            {...props}
            aria-label={ariaLabel}
            className={classnames(styles.numberPagesWrapper, {
                [styles.numberPagesWrapperWithoutNavigationButtons]:
                    !pages.includes(PageControls.Next) && !pages.includes(PageControls.Previous),
            })}
        >
            <ul className={styles.numberPagesContainer}>{pages.map(renderPageItem)}</ul>
        </nav>
    );
};
